import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Download, 
  FileText, 
  Database, 
  Users, 
  Music,
  Calendar,
  Settings,
  MessageSquare
} from 'lucide-react';
import { adminService, type AnnotationFilters } from '@/client/services/admin';
import { toast } from 'sonner';

interface ExportDialogProps {
  trigger?: React.ReactNode;
  defaultFilters?: AnnotationFilters;
  exportType?: 'annotations' | 'participants' | 'feedback' | 'both';
}

export function ExportDialog({ 
  trigger, 
  defaultFilters = {}, 
  exportType = 'annotations' 
}: ExportDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [format, setFormat] = useState<'csv' | 'json'>('csv');
  const [filters, setFilters] = useState<AnnotationFilters>(defaultFilters);
  const [includeParticipantData, setIncludeParticipantData] = useState(true);
  const [includeSongData, setIncludeSongData] = useState(false);
  const [includeAnnotationStats, setIncludeAnnotationStats] = useState(true);
  const [includeSessionData, setIncludeSessionData] = useState(false);
  
  // Feedback-specific filters
  const [feedbackFilters, setFeedbackFilters] = useState({
    processed: undefined as boolean | undefined,
    search: '',
    dateFrom: '',
    dateTo: '',
    context: '',
    participantId: ''
  });

  const handleExportAnnotations = async () => {
    setIsExporting(true);
    try {
      await adminService.downloadAnnotationExport(filters, format, {
        includeParticipantData,
        includeSongData
      });
      toast.success(`Annotation data exported successfully as ${format.toUpperCase()}`);
      setIsOpen(false);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export annotation data');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportParticipants = async () => {
    setIsExporting(true);
    try {
      await adminService.downloadParticipantExport(format, {
        includeAnnotationStats
      });
      toast.success(`Participant data exported successfully as ${format.toUpperCase()}`);
      setIsOpen(false);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export participant data');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportFeedback = async () => {
    setIsExporting(true);
    try {
      // Clean up empty filter values
      const cleanFilters = Object.fromEntries(
        Object.entries(feedbackFilters).filter(([_, value]) => value !== '' && value !== undefined)
      );
      
      await adminService.downloadFeedbackExport(cleanFilters, format, {
        includeParticipantData,
        includeSessionData
      });
      toast.success(`Feedback data exported successfully as ${format.toUpperCase()}`);
      setIsOpen(false);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export feedback data');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportBoth = async () => {
    setIsExporting(true);
    try {
      // Export annotations
      await adminService.downloadAnnotationExport(filters, format, {
        includeParticipantData,
        includeSongData
      });
      
      // Export participants
      await adminService.downloadParticipantExport(format, {
        includeAnnotationStats
      });
      
      // Export feedback
      const cleanFeedbackFilters = Object.fromEntries(
        Object.entries(feedbackFilters).filter(([_, value]) => value !== '' && value !== undefined)
      );
      await adminService.downloadFeedbackExport(cleanFeedbackFilters, format, {
        includeParticipantData,
        includeSessionData
      });
      
      toast.success(`All datasets exported successfully as ${format.toUpperCase()}`);
      setIsOpen(false);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExport = () => {
    switch (exportType) {
      case 'annotations':
        return handleExportAnnotations();
      case 'participants':
        return handleExportParticipants();
      case 'feedback':
        return handleExportFeedback();
      case 'both':
        return handleExportBoth();
      default:
        return handleExportAnnotations();
    }
  };

  const getExportDescription = () => {
    switch (exportType) {
      case 'annotations':
        return 'Export annotation data with optional participant demographics and song metadata.';
      case 'participants':
        return 'Export participant demographic data with optional annotation statistics.';
      case 'feedback':
        return 'Export participant feedback data with optional participant and session context.';
      case 'both':
        return 'Export annotation, participant, and feedback datasets as separate files.';
      default:
        return 'Export research data for analysis.';
    }
  };

  const getExportIcon = () => {
    switch (exportType) {
      case 'annotations':
        return <Music className="h-4 w-4" />;
      case 'participants':
        return <Users className="h-4 w-4" />;
      case 'feedback':
        return <MessageSquare className="h-4 w-4" />;
      case 'both':
        return <Database className="h-4 w-4" />;
      default:
        return <Download className="h-4 w-4" />;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="gap-2">
            <Download className="h-4 w-4" />
            Export Data
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getExportIcon()}
            Export Research Data
          </DialogTitle>
          <DialogDescription>
            {getExportDescription()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Format Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Export Format
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div 
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    format === 'csv' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-muted hover:border-primary/50'
                  }`}
                  onClick={() => setFormat('csv')}
                >
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      checked={format === 'csv'}
                      onChange={() => setFormat('csv')}
                      className="sr-only"
                    />
                    <div className="font-medium">CSV</div>
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    Comma-separated values, ideal for Excel and statistical software
                  </div>
                </div>
                
                <div 
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    format === 'json' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-muted hover:border-primary/50'
                  }`}
                  onClick={() => setFormat('json')}
                >
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      checked={format === 'json'}
                      onChange={() => setFormat('json')}
                      className="sr-only"
                    />
                    <div className="font-medium">JSON</div>
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    Structured data format, ideal for programming and APIs
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Filters (for annotations) */}
          {(exportType === 'annotations' || exportType === 'both') && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Annotation Filters
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="source">Source</Label>
                    <Select
                      value={filters.songSource || 'all'}
                      onValueChange={(value) =>
                        setFilters(prev => ({ ...prev, songSource: value === 'all' ? undefined : value }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="All sources" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All sources</SelectItem>
                        <SelectItem value="suno">Suno</SelectItem>
                        <SelectItem value="mureka">Mureka</SelectItem>
                        <SelectItem value="sonauto">Sonauto</SelectItem>
                        <SelectItem value="udio">Udio</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="authenticity">Authenticity</Label>
                    <Select
                      value={filters.authenticityAssessment || 'all'}
                      onValueChange={(value) =>
                        setFilters(prev => ({ ...prev, authenticityAssessment: value === 'all' ? undefined : value }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="All assessments" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All assessments</SelectItem>
                        <SelectItem value="real">Real</SelectItem>
                        <SelectItem value="ai-generated">AI-generated</SelectItem>
                        <SelectItem value="uncertain">Uncertain</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="startDate">Start Date</Label>
                    <Input
                      type="date"
                      value={filters.startDate || ''}
                      onChange={(e) =>
                        setFilters(prev => ({ ...prev, startDate: e.target.value || undefined }))
                      }
                    />
                  </div>

                  <div>
                    <Label htmlFor="endDate">End Date</Label>
                    <Input
                      type="date"
                      value={filters.endDate || ''}
                      onChange={(e) =>
                        setFilters(prev => ({ ...prev, endDate: e.target.value || undefined }))
                      }
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Feedback Filters */}
          {(exportType === 'feedback' || exportType === 'both') && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Feedback Filters
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="processed">Processing Status</Label>
                    <Select
                      value={feedbackFilters.processed === undefined ? 'all' : feedbackFilters.processed.toString()}
                      onValueChange={(value) =>
                        setFeedbackFilters(prev => ({ 
                          ...prev, 
                          processed: value === 'all' ? undefined : value === 'true' 
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="All feedback" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All feedback</SelectItem>
                        <SelectItem value="false">Unprocessed only</SelectItem>
                        <SelectItem value="true">Processed only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="context">Submission Context</Label>
                    <Select
                      value={feedbackFilters.context || 'all'}
                      onValueChange={(value) =>
                        setFeedbackFilters(prev => ({ 
                          ...prev, 
                          context: value === 'all' ? '' : value 
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="All contexts" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All contexts</SelectItem>
                        <SelectItem value="demographics">Demographics</SelectItem>
                        <SelectItem value="annotation">Annotation</SelectItem>
                        <SelectItem value="completion">Completion</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="feedbackStartDate">Start Date</Label>
                    <Input
                      type="date"
                      value={feedbackFilters.dateFrom}
                      onChange={(e) =>
                        setFeedbackFilters(prev => ({ ...prev, dateFrom: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <Label htmlFor="feedbackEndDate">End Date</Label>
                    <Input
                      type="date"
                      value={feedbackFilters.dateTo}
                      onChange={(e) =>
                        setFeedbackFilters(prev => ({ ...prev, dateTo: e.target.value }))
                      }
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="search">Search Feedback Text</Label>
                    <Input
                      placeholder="Search in feedback content..."
                      value={feedbackFilters.search}
                      onChange={(e) =>
                        setFeedbackFilters(prev => ({ ...prev, search: e.target.value }))
                      }
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Export Options */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Database className="h-4 w-4" />
                Export Options
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {(exportType === 'annotations' || exportType === 'both') && (
                <>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="includeParticipantData"
                      checked={includeParticipantData}
                      onCheckedChange={(checked) => setIncludeParticipantData(!!checked)}
                    />
                    <Label htmlFor="includeParticipantData" className="text-sm">
                      Include participant demographic data
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="includeSongData"
                      checked={includeSongData}
                      onCheckedChange={(checked) => setIncludeSongData(!!checked)}
                    />
                    <Label htmlFor="includeSongData" className="text-sm">
                      Include song metadata (title, duration, genres)
                    </Label>
                  </div>
                </>
              )}

              {(exportType === 'participants' || exportType === 'both') && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="includeAnnotationStats"
                    checked={includeAnnotationStats}
                    onCheckedChange={(checked) => setIncludeAnnotationStats(!!checked)}
                  />
                  <Label htmlFor="includeAnnotationStats" className="text-sm">
                    Include annotation statistics for each participant
                  </Label>
                </div>
              )}

              {(exportType === 'feedback' || exportType === 'both') && (
                <>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="includeFeedbackParticipantData"
                      checked={includeParticipantData}
                      onCheckedChange={(checked) => setIncludeParticipantData(!!checked)}
                    />
                    <Label htmlFor="includeFeedbackParticipantData" className="text-sm">
                      Include participant demographic data with feedback
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="includeSessionData"
                      checked={includeSessionData}
                      onCheckedChange={(checked) => setIncludeSessionData(!!checked)}
                    />
                    <Label htmlFor="includeSessionData" className="text-sm">
                      Include annotation session context and progress
                    </Label>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={isExporting} className="gap-2">
            {isExporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Exporting...
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                Export {format.toUpperCase()}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}